import React, { useEffect } from 'react';
import {
  VStack,
  Grid,
  Text,
  Divider,
  HStack,
  Icon,
  Box,
} from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';
import useQueryParams from '@common/src/hooks/useQueryParams';
import { useFlowsSession } from '@flows/src/api/getFlowsSessionById';
import { useParty } from '@parties/src/features/detail/api/getParty';
import { getPartyName } from '@parties/src/utils/partyUtils';
import BrokerCardContainer from '@flows/src/components/ui/common/components/finalization/BrokerCardContainer';
import AssistantQuestion from '../../../../sales-conversation/src/components/flow/AssistantQuestion';
import useAuth from '../../../../app/src/features/authentication/hooks/useAuth';
import { Party, PartyType } from '@parties/src/types';
import AnvaProductPackagesOverview from '@flows/src/pages/flow-products/AnvaProductPackagesOverview';
import AnvaProductPoliciesOverview from '@flows/src/pages/flow-products/AnvaProductPoliciesOverview';
import useSessionId from '@flows/src/hooks/useSessionId';
import { useCompleteConversation } from '@conversations/src/features/detail/shared/api/completeConversation';
import { useConversation } from '@conversations/src/features/detail/shared/api/getConversationById';
import { useFlowInfo } from '@flows/src/api/getFlowInfo';
import { ConversationStatus } from '@conversations/src/features/overview/types/OverviewTypes';

const LabeledValue: React.FC<{ label: string; value?: string }> = ({
  label,
  value,
}) => {
  if (!value) return null;
  return (
    <Box>
      <Text textStyle="smallBody" color="gray.500">
        {label}
      </Text>
      <Text textStyle="normalBody">{value}</Text>
    </Box>
  );
};

const PartySummaryCard: React.FC<{ party?: Party }> = ({ party }) => {
  const { t } = useTranslation();
  if (!party) return null;

  const isCompany = party.type === 'COMPANY';
  const displayName = getPartyName(party);
  const email = party.email;
  const telephonenr = party.telephonenr;
  const companyNr = party.company_registration?.nr || party.cbe || undefined;

  // Contact person for companies (fields may vary by backend version)
  type ContactPersonExtra = {
    first_name?: string;
    initials?: string;
    last_name?: string;
    birth?: string;
    email?: string;
    telephonenr?: string;
    name?: string;
  };
  const contactPerson = (party.contact_person ||
    {}) as unknown as ContactPersonExtra;
  const contactNameParts = [
    contactPerson.first_name || contactPerson.initials,
    contactPerson.last_name,
  ].filter(Boolean);
  const contactName = contactNameParts.join(' ') || contactPerson.name || '';

  return (
    <Box borderWidth="1px" borderColor="gray.50" borderRadius="6" p="4">
      <VStack align="stretch" spacing="4">
        <Divider borderColor="gray.50" />
        <HStack>
          <Icon
            name={
              isCompany ? 'GeneralNavigationOfficeInfo' : 'GeneralOthersAvatar'
            }
            boxSize={4}
          />
          <Text textStyle="normalBodyMedium">{displayName}</Text>
        </HStack>
        <Grid
          gridGap="4"
          templateColumns={{
            base: 'repeat(1, 1fr)',
            mobileL: 'repeat(2, 1fr)',
          }}
        >
          {isCompany ? (
            <>
              <LabeledValue
                label={t('PARTY-CREATION_LEGAL_COMPANY-NAME')}
                value={displayName}
              />
              <LabeledValue
                label={t('COMPANY-REGISTRATION-NUMBER')}
                value={companyNr}
              />
              <LabeledValue label={t('EMAIL')} value={email} />
              <LabeledValue label={t('TELEPHONE-NUMBER')} value={telephonenr} />
            </>
          ) : (
            <>
              <LabeledValue label={t('NAME')} value={displayName} />
              <LabeledValue label={t('EMAIL')} value={email} />
              <LabeledValue label={t('TELEPHONE-NUMBER')} value={telephonenr} />
            </>
          )}
        </Grid>

        {isCompany &&
          (contactName || contactPerson.email || contactPerson.telephonenr) && (
            <>
              <Divider borderColor="gray.50" />
              <HStack>
                <Icon name="GeneralOthersAvatar" boxSize={4} />
                <Text textStyle="normalBodyMedium">
                  {t(
                    'PORTFOLIO_COMPANY-DETAIL_CONTACT-INFORMATION_CONTACT-PERSON-AT-COMPANY',
                  )}
                </Text>
              </HStack>
              <Grid
                gridGap="4"
                templateColumns={{
                  base: 'repeat(1, 1fr)',
                  mobileL: 'repeat(2, 1fr)',
                }}
              >
                <LabeledValue label={t('NAME')} value={contactName} />
                <LabeledValue
                  label={t('DATE-OF-BIRTH')}
                  value={contactPerson.birth}
                />
                <LabeledValue label={t('EMAIL')} value={contactPerson.email} />
                <LabeledValue
                  label={t('TELEPHONE-NUMBER')}
                  value={contactPerson.telephonenr}
                />
              </Grid>
            </>
          )}
      </VStack>
    </Box>
  );
};

const YellowhiveFinalization: React.FC = () => {
  const { t } = useTranslation();
  const { distribution } = useAuth();
  const conversationId = useSessionId();
  const queryparams = useQueryParams();
  const sessionId = queryparams.get('session_id') || '';
  const { data: session } = useFlowsSession(sessionId);
  const { data: conversation } = useConversation(sessionId);
  const { data: flowInfo } = useFlowInfo(session?.flow_id || '');
  const partyId = session?.prospect?.id || '';
  const partyType = session?.prospect?.type || '';
  const { data: party } = useParty(partyId);

  // Complete the conversation on entry, but avoid re-triggering on refresh if already completed
  const { mutate, isIdle } = useCompleteConversation(conversationId, false);
  useEffect(() => {
    if (
      isIdle &&
      conversation &&
      conversation.status !== ConversationStatus.COMPLETED
    )
      mutate(undefined);
  }, [conversation]);

  // Determine which session should be used to fetch policies/packages
  const previousFlowId = flowInfo?.previous;
  const linkedSessions = conversation?.linked_sessions || [];
  const previousLinked = linkedSessions.find(
    (ls) => ls.flow_id === previousFlowId,
  );
  const policiesSessionId = previousLinked?.id || sessionId;

  return (
    <Box w="100%" maxW="6xl" mx="auto" py="6" px="8">
      <VStack align="stretch" spacing="6">
        <Box>
          <AssistantQuestion
            question={t('FLOWS_CAMPAIGNS_FINALIZATION_TITLE', {
              distribution: distribution?.name,
            })}
          />
        </Box>

        <PartySummaryCard party={party} />

        <VStack align="stretch" spacing="6">
          <AnvaProductPackagesOverview
            partyId={partyId}
            sessionId={policiesSessionId}
            partyType={partyType as PartyType}
            editable={false}
          />
          <AnvaProductPoliciesOverview
            partyId={partyId}
            sessionId={policiesSessionId}
            partyType={partyType as PartyType}
            editable={false}
          />
        </VStack>

        <BrokerCardContainer />
      </VStack>
    </Box>
  );
};

export default YellowhiveFinalization;
